import {
  normalizeDestinationName,
  sortQuotesByDlvdOfferOld,
} from "../panels/multiLocationQuotePanel/components/multiLocationQuoteUtils";
import {
  IGetQuotesResponseOld,
  IQuotesByDestinationOld,
  IDestination,
  IQuoteExtensionOld,
} from "../types/ICompAnalysisQMD";
import ILocation from "../types/ILocation";
import { removeCity } from "./crap";
import NitroService from "./nitro.service";
import { toCamelCase } from "./String";

const generateDisplayName = (
  destination: IDestination,
  isCity: boolean,
  destinationDetails?: ILocation,
) => {
  const { city, stateOrProvince, name } = destination;
  // if isCity is true city will also be defined
  if (isCity && city) {
    return `${toCamelCase(city)}, ${stateOrProvince}`;
  }
  if (!destinationDetails) return undefined;
  const { pinType } = destinationDetails;
  if (pinType === "customer") {
    return removeCity(name, destinationDetails.city || "")
      .split(" ")
      .map(toCamelCase)
      .join(" ");
  }
};

const findAvailableProducts = (details?: ILocation) => {
  if (!details) return [];
  const productsSet = new Set<string>();
  details.occupants.forEach((occupant) => {
    occupant.products.forEach((product) => {
      productsSet.add(product.product);
    });
  });
  return Array.from(productsSet);
};

const extendQuotes = (
  quotesToExtend: IGetQuotesResponseOld[],
  destinationDetailsMap: Map<string, ILocation>,
  cityZipMap: Map<string, string>,
  isSurroundingLocation = false,
) => {
  return quotesToExtend.map((entry) => {
    const { destination, quotes } = entry;

    // resolve origin
    const extendedQuotes: IQuoteExtensionOld[] = quotes.map((quote) => {
      return {
        ...quote,
        initialOffer:
          !quote.freight || !quote.maxPrice
            ? undefined
            : (quote.maxPrice + quote.freight.totalFreight).toFixed(0),
      };
    });

    // resolve destination
    let destinationDetails: ILocation | undefined;
    if (!destination.isCity) {
      destinationDetails = destinationDetailsMap.get(destination.id);
      if (!destinationDetails) return null;
    }

    return {
      destination: {
        ...destination,
        details: destinationDetails,
        availableProducts: findAvailableProducts(destinationDetails),
        isSurroundingLocation,
        displayName:
          generateDisplayName(
            destination,
            destination.isCity,
            destinationDetails,
          ) || destination.name,
        city: destination.isCity
          ? destination.city
          : destinationDetails?.city || "",
        stateOrProvince: destination.isCity
          ? destination.stateOrProvince
          : destinationDetails?.state || "",
        pinType: destination.isCity
          ? "city"
          : destinationDetails?.pinType || "",
        zipCode: destination.isCity
          ? cityZipMap.get(destination.id)
          : undefined,
      },
      quotes: sortQuotesByDlvdOfferOld(extendedQuotes),
    } as IQuotesByDestinationOld;
  });
};

// fetch freight zip codes for city-pin destinations (no location details exist for these)
const getCityZipMap = async (
  quotes: IGetQuotesResponseOld[],
): Promise<Map<string, string>> => {
  const cityZipMap = new Map<string, string>();
  const cityDestinations = quotes.filter((entry) => entry.destination.isCity);

  await Promise.all(
    cityDestinations.map(async ({ destination }) => {
      if (!destination.city || !destination.stateOrProvince) return;

      const cityState = `${normalizeDestinationName(
        destination.city,
      )}, ${destination.stateOrProvince.toUpperCase()}`;

      try {
        const zipCode = await NitroService.getCityStateFreightZip(cityState);
        if (zipCode) {
          cityZipMap.set(destination.id, zipCode);
        }
      } catch (error) {
        console.error("Failed to fetch city state freight zip", error);
      }
    }),
  );

  return cityZipMap;
};

export const convertCompAnalysisQMDOld = async (
  quotes: IGetQuotesResponseOld[],
  isSurroundingLocation = false,
): Promise<IQuotesByDestinationOld[]> => {
  // get destination details for non-city destinations in both primary and surrounding locations
  const nonCityIds = quotes
    .filter((entry) => !entry.destination.isCity)
    .map((entry) => entry.destination.id);

  const destinationDetailsMap = new Map<string, ILocation>();

  const [, cityZipMap] = await Promise.all([
    (async () => {
      if (nonCityIds.length > 0) {
        try {
          const allDestinationDetails =
            await NitroService.getMultiLocationDetails({
              ids: nonCityIds,
            });

          if (allDestinationDetails) {
            allDestinationDetails.forEach((detail) => {
              if (detail?.id) {
                destinationDetailsMap.set(detail.id, detail);
              }
            });
          }
        } catch (error) {
          console.error("Failed to fetch batch destination details", error);
        }
      }
    })(),
    getCityZipMap(quotes),
  ]);

  const extendedQuotes = extendQuotes(
    quotes,
    destinationDetailsMap,
    cityZipMap,
    isSurroundingLocation,
  );

  return extendedQuotes.filter(
    (quote): quote is IQuotesByDestinationOld => quote !== null,
  );
};
